// src/hooks/useWalkingRoute.ts
//
// Fetches a walking route from Google Directions API and returns the
// decoded polyline path. Cached in-memory keyed by destination + user
// coords rounded to 4 decimals (~11m). Within that bucket the route is
// the same, so we hit memory not the API on most card transitions.
//
// Returns null path when:
//   - Either endpoint is missing
//   - Maps SDK hasn't loaded yet
//   - Directions request failed (graceful — caller falls back to no
//     polyline, which is still a useful map)

import { useEffect, useState } from 'react';
import { useApiIsLoaded, useMapsLibrary } from '@vis.gl/react-google-maps';

interface Coord {
  lat: number;
  lng: number;
}

interface RouteResult {
  polyline: Coord[] | null;
  distanceM: number | null;
  durationS: number | null;
}

const cache = new Map<string, RouteResult>();
const inflight = new Map<string, Promise<RouteResult>>();

function bucketKey(dest: Coord, user: Coord): string {
  const r = (n: number) => n.toFixed(4);
  return `${r(dest.lat)},${r(dest.lng)}|${r(user.lat)},${r(user.lng)}`;
}

export function useWalkingRoute(
  destination: Coord | null,
  user: Coord | null
): RouteResult {
  const [result, setResult] = useState<RouteResult>({
    polyline: null,
    distanceM: null,
    durationS: null,
  });
  const isLoaded = useApiIsLoaded();
  const routesLib = useMapsLibrary('routes');

  useEffect(() => {
    if (!destination || !user || !isLoaded || !routesLib) {
      setResult({ polyline: null, distanceM: null, durationS: null });
      return;
    }

    const key = bucketKey(destination, user);
    const cached = cache.get(key);
    if (cached) {
      setResult(cached);
      return;
    }

    let cancelled = false;
    const existing = inflight.get(key);
    const promise: Promise<RouteResult> =
      existing ||
      (async () => {
        try {
          const ds = new routesLib.DirectionsService();
          const resp = await ds.route({
            origin: user,
            destination,
            travelMode: google.maps.TravelMode.WALKING,
          });
          const route = resp.routes?.[0];
          const leg = route?.legs?.[0];
          if (!route || !leg) {
            const empty = {
              polyline: null,
              distanceM: null,
              durationS: null,
            };
            cache.set(key, empty);
            return empty;
          }
          const path: Coord[] = (route.overview_path || []).map((p) => ({
            lat: p.lat(),
            lng: p.lng(),
          }));
          const out: RouteResult = {
            polyline: path,
            distanceM: leg.distance?.value ?? null,
            durationS: leg.duration?.value ?? null,
          };
          cache.set(key, out);
          return out;
        } catch (err) {
          console.warn('useWalkingRoute: Directions failed', err);
          const empty = {
            polyline: null,
            distanceM: null,
            durationS: null,
          };
          // Cache failures briefly so we don't hammer on retries
          cache.set(key, empty);
          return empty;
        } finally {
          inflight.delete(key);
        }
      })();
    inflight.set(key, promise);
    promise.then((r) => {
      if (!cancelled) setResult(r);
    });

    return () => {
      cancelled = true;
    };
  }, [
    destination?.lat,
    destination?.lng,
    user?.lat,
    user?.lng,
    isLoaded,
    routesLib,
  ]);

  return result;
}
